import type { SupabaseClient } from '@supabase/supabase-js';
import { costear, type Costeo, type FilaFormula, type Material } from '../logica/costeo';

export interface Producto { sku: string; nombre: string; categoria_id: number | null; grupo_talla: string; descripcion: string | null; colores: string[] | null; activo: boolean }
export interface Categoria { id: number; nombre: string; familia_id: number | null }
export interface ProductoCosteado extends Producto { categoria: string; precios: Record<string, number>; costeo: Costeo }

// Lee el catálogo y calcula el costo de cada prenda con la misma regla del prototipo.
// El promedio de compras sale de la vista v_costo_material (no hace falta traer todas las compras).
export async function cargarCatalogo(sb: SupabaseClient): Promise<ProductoCosteado[]> {
  const [prod, cat, fam, pre, mat, bom, prom] = await Promise.all([
    sb.from('productos').select('sku, nombre, categoria_id, grupo_talla, descripcion, colores, activo').order('sku'),
    sb.from('categorias').select('id, nombre, familia_id').eq('ambito', 'producto'),
    sb.from('familias').select('id, nombre').eq('ambito', 'producto'),
    sb.from('precios').select('sku, moneda, valor'),
    sb.from('materiales').select('codigo, nombre, tipo, unidad, costo_estandar'),
    sb.from('insumos_producto').select('sku, codigo, cantidad, costo_servicio, costo_ref_2025'),
    sb.from('v_costo_material').select('codigo, costo_promedio'),
  ]);
  const error = [prod, cat, fam, pre, mat, bom, prom].find(r => r.error)?.error;
  if (error) throw new Error(error.message);

  const familias = new Map((fam.data || []).map(f => [f.id as number, f.nombre as string]));
  const categorias = new Map((cat.data as Categoria[] || []).map(c => [c.id, c.familia_id && familias.get(c.familia_id) ? `${familias.get(c.familia_id)} · ${c.nombre}` : c.nombre]));
  const materiales = new Map((mat.data as Material[] || []).map(m => [m.codigo, m]));
  const promedio = new Map<string, number>();
  for (const r of prom.data || []) if (+r.costo_promedio > 0) promedio.set(r.codigo, +r.costo_promedio);
  const precios = new Map<string, Record<string, number>>();
  for (const r of pre.data || []) { const p = precios.get(r.sku) || {}; p[String(r.moneda).trim()] = +r.valor; precios.set(r.sku, p); }
  const formula = new Map<string, FilaFormula[]>();
  for (const r of (bom.data as FilaFormula[] || [])) { if (!formula.has(r.sku)) formula.set(r.sku, []); formula.get(r.sku)!.push(r); }

  return (prod.data as Producto[] || []).map(p => ({
    ...p,
    categoria: (p.categoria_id != null && categorias.get(p.categoria_id)) || '',
    precios: precios.get(p.sku) || {},
    costeo: costear(formula.get(p.sku) || [], materiales, promedio),
  }));
}

// ---------- Edición de productos, fórmula y precios (7.3f) ----------
const ok = ({ error }: { error: { message: string } | null }) => { if (error) throw new Error(error.message); };

export interface Insumo extends Material { categoria_id: number | null; stock_minimo: number | null; notas: string | null }
export async function cargarInsumos(sb: SupabaseClient): Promise<Insumo[]> {
  const { data, error } = await sb.from('materiales').select('codigo, nombre, tipo, unidad, costo_estandar, categoria_id, stock_minimo, notas').order('codigo');
  if (error) throw new Error(error.message);
  return (data || []).map(m => ({ ...m, costo_estandar: m.costo_estandar == null ? null : +m.costo_estandar, stock_minimo: m.stock_minimo == null ? null : +m.stock_minimo })) as Insumo[];
}
export async function categoriasDe(sb: SupabaseClient, ambito: 'producto' | 'material' | 'gasto'): Promise<{ id: number; nombre: string; logistica: boolean }[]> {
  const [cat, fam] = await Promise.all([sb.from('categorias').select('id, nombre, familia_id, logistica').eq('ambito', ambito).order('nombre'), sb.from('familias').select('id, nombre').eq('ambito', ambito)]);
  const error = cat.error || fam.error;
  if (error) throw new Error(error.message);
  const f = new Map((fam.data || []).map(x => [x.id as number, x.nombre as string]));
  return (cat.data || []).map(c => ({ id: c.id, nombre: c.familia_id && f.get(c.familia_id) ? `${f.get(c.familia_id)} · ${c.nombre}` : c.nombre, logistica: !!c.logistica }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

// Producto nuevo: el siguiente TAL-0xx, inactivo hasta tener fórmula y precio.
export async function crearProducto(sb: SupabaseClient): Promise<string> {
  const { data, error } = await sb.from('productos').select('sku');
  if (error) throw new Error(error.message);
  const n = (data || []).reduce((m, p) => Math.max(m, +String(p.sku).replace(/\D/g, '') || 0), 0) + 1, sku = 'TAL-' + String(n).padStart(3, '0');
  ok(await sb.from('productos').insert({ sku, nombre: 'Producto nuevo', grupo_talla: 'UNICA', activo: false }));
  return sku;
}
export async function cambiarProducto(sb: SupabaseClient, sku: string, cambios: Partial<Omit<Producto, 'sku'>>) { ok(await sb.from('productos').update(cambios).eq('sku', sku)); }
// Un precio vacío o en cero se quita: el producto deja de ofrecerse en esa moneda.
export async function guardarPrecio(sb: SupabaseClient, sku: string, moneda: string, valor: number | null) {
  ok(valor && valor > 0 ? await sb.from('precios').upsert({ sku, moneda, valor }, { onConflict: 'sku,moneda' }) : await sb.from('precios').delete().eq('sku', sku).eq('moneda', moneda));
}
export async function agregarInsumo(sb: SupabaseClient, sku: string, codigo: string) { ok(await sb.from('insumos_producto').insert({ sku, codigo, cantidad: null })); }
export async function cambiarInsumo(sb: SupabaseClient, sku: string, codigo: string, cambios: Partial<Pick<FilaFormula, 'cantidad' | 'costo_servicio'>>) {
  ok(await sb.from('insumos_producto').update(cambios).eq('sku', sku).eq('codigo', codigo));
}
export async function quitarInsumo(sb: SupabaseClient, sku: string, codigo: string) { ok(await sb.from('insumos_producto').delete().eq('sku', sku).eq('codigo', codigo)); }
// Copia los insumos de otra prenda que esta todavía no tiene (con sus cantidades, para ajustarlas después).
export async function copiarFormula(sb: SupabaseClient, desde: string, hacia: string): Promise<number> {
  const [a, b] = await Promise.all([sb.from('insumos_producto').select('codigo, cantidad, costo_servicio, costo_ref_2025').eq('sku', desde), sb.from('insumos_producto').select('codigo').eq('sku', hacia)]);
  const error = a.error || b.error;
  if (error) throw new Error(error.message);
  const ya = new Set((b.data || []).map(x => x.codigo)), nuevas = (a.data || []).filter(x => !ya.has(x.codigo)).map(x => ({ ...x, sku: hacia }));
  if (nuevas.length) ok(await sb.from('insumos_producto').insert(nuevas));
  return nuevas.length;
}
